import { COMMON_COMPONENT_TYPES } from "constants/flowDesigner";
import { ComponentPropsContext } from "layouts/Flow Designer/context";
import { Fragment, useContext } from "react";
import { useSelector } from "react-redux";
import SelectionComponentForm from "../SelectionComponentForm";
import { styled } from "@mui/material/styles";
import { Box, Divider, List, ListItem } from "@mui/material";
import { NODE_TYPES, ACTION_TYPES } from "constants/flowDesigner";
import { noInputOperators, styles } from "../..";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DropdownListItem from "../DropdownListItem";
import CheckboxListItem from "../CheckboxListItem";
import ButtonListItem from "../ButtonListItem";
import FileListItem from "../FileListItem";
import ReadOnlyListItem from "../ReadOnlyListItem";
import RadioGroupListItem from "../RadioGroupListItem";
import TagListItem from "../TagListItem";
import { CreatableSelect } from "../CreatableSelect";
import CodeEditorInput from "../CodeEditorInput";
import PayloadEditorInput from "../PayloadEditorInput";
import ErrorHandlingDropdownListItem from "../ErrorHandlingDropdownListItem";
import SqlEditorInput from "../SqlEditorInput";
import { SimpleTag } from "../SimpleTag";
import CredentialListItem from "../CredentialListItem";
import { StringInput } from "../StringInput";
import { TextInput } from "../TextInput";
import { KeyValueInput } from "../KeyValueInput";
import { PasswordInput } from "../PasswordInput";
import { NumberInput } from "../NumberInput";
import { combineStyles, formatGroupName } from "helpers/common";
import SelectWithDetailsInput from "../SelectWithDetailsInput";

const typeToIncludeWithTransform = ["text", "tag", "credential", "number"];

const ActionFieldList = styled(List)(() => ({
   overflow: "hidden",
   padding: "0 8px 8px 4px"
}));

const ConnectorFieldGroup = styled(Box)(({ theme }) => ({
   width: "100%",
   border: `1px solid ${theme.palette.divider}`,
   borderRadius: theme.spacing(1.5),
   padding: `${theme.spacing(0.5)} ${theme.spacing(2)} ${theme.spacing(1.5)}`,
   marginTop: theme.spacing(1.5),
   minWidth: 0,
   backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.015)" : "rgba(0, 0, 0, 0.015)"
}));

export const FormBody = ({ codeEditorInputRef, sqlEditorInputRef, actionFieldsRef, tabId, selectionRef }) => {
   const { currentSavedFlow } = useSelector((store) => store?.designer?.flows);
   const tabState = useSelector((store) => store?.designer?.componentPropsPanel?.tabs?.[tabId] || {});
   const { selectedNode } = tabState;
   const { componentAction, actionForm } = useContext(ComponentPropsContext);
   const { allowEditWorkflow } = useAllowEditWorkflow();
   const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);
   const { type } = selectedNode;
   const isCommonComponent = type === NODE_TYPES.COMMON || selectedNode.type === NODE_TYPES.LABEL;
   const isMultipleActions = selectedNode?.actions?.length > 1;
   const isFlowActive = currentSavedFlow?.shared_status === "active";
   const firstActionType = componentAction?.dataInsideAction?.length ? componentAction.dataInsideAction[0].type : "";

   if (selectedNode.key === COMMON_COMPONENT_TYPES.SELECTION) return <SelectionComponentForm tabId={tabId} ref={selectionRef} />;

   const getFieldValue = (action) => {
      const defaultValue = action.type === ACTION_TYPES.KEY_VALUE ? [] : action?.value;
      const selectedValue = actionForm[action?.field];
      return selectedValue?.value !== undefined && selectedValue?.value !== null ? selectedValue.value : defaultValue;
   };

   const generateStringInput = (action) => {
      return (
         <ListItem key={action.field ?? action.label} sx={styles.w100}>
            <StringInput action={action} getFieldValue={getFieldValue} />
         </ListItem>
      );
   };

   const generateDropdownListItem = (action) => {
      if (selectedNode.key === COMMON_COMPONENT_TYPES.CODE && action.field === "language") {
         return null;
      }
      return <DropdownListItem key={action.label} selectedNode={selectedNode} action={action} fieldValue={getFieldValue(action)} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />;
   };

   const generateCheckboxListItem = (action) => {
      return <CheckboxListItem key={action.label} action={action} fieldValue={getFieldValue(action)} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />;
   };

   const generateButtonListItem = (action) => {
      if (isFlowActive) return null;
      const isMcpOAuthAuthentication = selectedNode.key === COMMON_COMPONENT_TYPES.MCP_CLIENT && action.field === "authenticateMcpOAuth";
      const hasOAuthConnection = Boolean(actionForm.oauthConnectionId?.value);

      return (
         <Fragment key={action.label}>
            <ButtonListItem key={action.label} action={action} selectedNode={selectedNode} actionFieldsRef={actionFieldsRef} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
            {isMcpOAuthAuthentication && !hasOAuthConnection && (
               <SoftTypography
                  key={`${action.field}-hint`}
                  variant="caption"
                  color="secondary"
                  sx={{ display: "block", px: 2, pt: 2, pb: 2, textAlign: "center" }}
               >
                  Authenticate with this MCP server before configuring its tools and arguments.
               </SoftTypography>
            )}
         </Fragment>
      );
   };

   const generateTextInput = (action) => {
      const hideForConditional = selectedNode.key === COMMON_COMPONENT_TYPES.CONDITIONAL && action.field === "valueToCompareWith" && noInputOperators.includes(actionForm.operator?.value);

      let isVisible = true;

      if (hideForConditional) {
         isVisible = false;
      }

      return (
         isVisible && (
            <ListItem key={action.label}>
               <TextInput action={action} getFieldValue={getFieldValue} />
            </ListItem>
         )
      );
   };

   const generateListInput = (action) => {
      const { label, listItems = [] } = action;
      return (
         <ListItem key={label} sx={styles.w100}>
            <List sx={styles.w100}>
               <SoftTypography
                  key={label}
                  variant="h6"
                  fontWeight="regular"
                  color="secondary"
                  sx={
                     (action.field === "payloadContentType" || action.field === "authenticationType") && {
                        fontSize: "12px",
                        fontWeight: "normal",
                        marginBottom: "-30px",
                        marginLeft: "15px",
                        marginTop: "20px"
                     }
                  }
               >
                  {label}
               </SoftTypography>
               {listItems.map((action) => getInputByType(action))}
            </List>
         </ListItem>
      );
   };

   const generateKeyValueInput = (action) => {
      const fieldValue = getFieldValue(action);

      return (
         <ListItem key={action.label} sx={combineStyles(styles.gap, styles.listItemMargin_t16)}>
            <SoftBox display="flex" flexDirection="column" width="100%" gap={2}>
               {fieldValue?.map((item, index) => (
                  <KeyValueInput key={`key-vale-${index}`} action={action} item={item} itemIndex={index} />
               ))}
            </SoftBox>
         </ListItem>
      );
   };

   const generateDivider = (action) => {
      return <Divider key={action.key} />;
   };

   const generateFileListItem = (action) => {
      return <FileListItem key={action.label} action={action} fieldValue={getFieldValue(action)} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />;
   };

   const generateReadOnlyListItem = (action) => {
      return <ReadOnlyListItem key={action.label} action={action} fieldValue={getFieldValue(action)} />;
   };

   const generateRadioGroupListItem = (action) => {
      return <RadioGroupListItem key={action.label} isHorizontal={true} action={action} fieldValue={getFieldValue(action)} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />;
   };

   const generateTagListItem = (action) => {
      return <TagListItem key={action.label} action={action} fieldValue={getFieldValue(action)} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />;
   };

   const generateSimpleTag = (action) => {
      const fieldValue = getFieldValue(action);
      return <SimpleTag key={action.label} action={action} fieldValue={fieldValue ? fieldValue : []} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />;
   };

   const generateCreatableSelect = (action) => {
      const fieldValue = getFieldValue(action);
      return <CreatableSelect key={action.label} action={action} fieldValue={fieldValue ? fieldValue : []} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />;
   };

   const generateCodeEditorInput = (action) => {
      return (
         <ListItem key={action.label} sx={styles.listItemMargin_t16}>
            <CodeEditorInput action={action} fieldValue={getFieldValue(action)} ref={codeEditorInputRef} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
         </ListItem>
      );
   };

   const generateSQLInput = (action) => {
      return (
         <ListItem key={action.label} sx={combineStyles(styles.w100, styles.listItemMargin_t16)}>
            <SqlEditorInput action={action} fieldValue={getFieldValue(action)} styles={styles} ref={sqlEditorInputRef} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
         </ListItem>
      );
   };

   const generateCredentialListItem = (action) => {
      return (
         <ListItem key={action.label} sx={combineStyles(styles.w100, styles.listItemMargin_t16)}>
            <CredentialListItem action={action} fieldValue={getFieldValue(action)} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
         </ListItem>
      );
   };

   const generateEditorInput = (action) => {
      return (
         <ListItem key={action.label} sx={combineStyles(styles.w100, styles.listItemMargin_t16)}>
            <PayloadEditorInput action={action} fieldValue={getFieldValue(action)} label={action.label} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
         </ListItem>
      );
   };

   const generateErrorHandlingDropdownListitem = (action) => {
      return (
         <ListItem key={action.label} sx={combineStyles(styles.w100, styles.listItemMargin_t16)}>
            <ErrorHandlingDropdownListItem action={action} fieldValue={getFieldValue(action)} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
         </ListItem>
      );
   };

   const generateJsonInput = (action) => {
      return (
         <ListItem key={action.label} sx={combineStyles(styles.w100, styles.listItemMargin_t16)}>
            <PayloadEditorInput action={action} fieldValue={getFieldValue(action)} label={action.label} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
         </ListItem>
      );
   };

   const generatePasswordInput = (action) => {
      return (
         <ListItem key={action.label} sx={combineStyles(styles.w100, styles.listItemMargin_t16)}>
            <PasswordInput action={action} getFieldValue={getFieldValue} />
         </ListItem>
      );
   };

   const generateNumberInput = (action) => {
      return (
         <ListItem key={action.label}>
            <NumberInput action={action} fieldValue={getFieldValue(action)} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
         </ListItem>
      );
   };

   const generateSelectWithDetailsInput = (action) => {
      return <SelectWithDetailsInput action={action} options={action.options} fieldValue={getFieldValue(action)} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />;
   };

   const getInputByType = (action) => {
      if (selectedNode.key === COMMON_COMPONENT_TYPES.MCP_CLIENT && action.field === "authenticateMcpOAuth") {
         return null;
      }

      if (action.hideUntilOAuthAuthenticated && actionForm.authenticationType?.value === "oauth" && !actionForm.oauthConnectionId?.value) {
         return null;
      }

      const visibleWhen = action.visibleWhen;

      if (visibleWhen && actionForm[visibleWhen.field]?.value !== visibleWhen.value) {
         return null;
      }

      switch (action.type) {
         case ACTION_TYPES.STRING:
            return generateStringInput(action);
         case ACTION_TYPES.PASSWORD:
            return generatePasswordInput(action);
         case ACTION_TYPES.JSON:
            return generateJsonInput(action);
         case ACTION_TYPES.DROPDOWN:
            return generateDropdownListItem(action);
         case ACTION_TYPES.CHECKBOX:
            return generateCheckboxListItem(action);
         case ACTION_TYPES.BUTTON:
            return generateButtonListItem(action);
         case ACTION_TYPES.TEXT:
            return generateTextInput(action);
         case ACTION_TYPES.LIST:
            return generateListInput(action);
         case ACTION_TYPES.KEY_VALUE:
            return generateKeyValueInput(action);
         case ACTION_TYPES.DIVIDER:
            return generateDivider(action);
         case ACTION_TYPES.FILE:
            return generateFileListItem(action);
         case ACTION_TYPES.READ_ONLY:
            return generateReadOnlyListItem(action);
         case ACTION_TYPES.RADIO_GROUP:
            return generateRadioGroupListItem(action);
         case ACTION_TYPES.TAG:
            return generateTagListItem(action);
         case ACTION_TYPES.SIMPLE_TAG:
            return generateSimpleTag(action);
         case ACTION_TYPES.CREATABLE_SELECT:
            return generateCreatableSelect(action);
         case ACTION_TYPES.CODE:
            return generateCodeEditorInput(action);
         case ACTION_TYPES.SQL:
            return generateSQLInput(action);
         case ACTION_TYPES.CREDENTIAL:
            return generateCredentialListItem(action);
         case ACTION_TYPES.EDITOR:
            return generateEditorInput(action);
         case ACTION_TYPES.ERROR_HANDLING_DROPDOWN:
            return generateErrorHandlingDropdownListitem(action);
         case ACTION_TYPES.NUMBER:
            return generateNumberInput(action);
         case ACTION_TYPES.SELECT_WITH_DETAILS:
            return generateSelectWithDetailsInput(action);
         default:
            return null;
      }
   };

   const getGroupName = (action) => {
      const groupNames = action.groupname;

      if (typeof groupNames === "string") return groupNames.trim();

      if (Array.isArray(groupNames)) {
         const groupName = groupNames.find((name) => typeof name === "string" && name.trim());
         return groupName?.trim() ?? "";
      }

      return "";
   };

   const generateConnectorFields = (actions) => {
      const sections = [];

      actions.forEach((action) => {
         const groupName = getGroupName(action);
         const previousSection = sections[sections.length - 1];

         if (groupName && previousSection?.groupName === groupName) {
            previousSection.actions.push(action);
            return;
         }

         sections.push({ groupName, actions: [action] });
      });

      return sections.map(({ groupName, actions: groupActions }, index) => {
         if (!groupName) return groupActions.map((action) => getInputByType(action));

         return (
            <ListItem key={`${groupName}-${index}`} disableGutters sx={{ display: "block", py: 0 }}>
               <ConnectorFieldGroup component="fieldset">
                  <Box
                     component="legend"
                     sx={{
                        px: 1,
                        ml: 0.5,
                        color: "text.secondary",
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase"
                     }}
                  >
                     {formatGroupName(groupName)}
                  </Box>
                  <List disablePadding>{groupActions.map((action) => getInputByType(action))}</List>
               </ConnectorFieldGroup>
            </ListItem>
         );
      });
   };

   const generateFields = () => {
      const actions = componentAction.dataInsideAction;

      if (componentAction.actionName === "Variables") {
         const variableActions = actions.filter((action) => action.field?.startsWith("var_"));
         const outputReferenceActions = actions.filter((action) => action.field?.startsWith("reference_var_"));
         const outputReferenceButton = actions.find((action) => action.field === "addOutputReference");
         const errorHandlingAction = actions.find((action) => action.field === "errorHandling");
         const nonVariableActions = actions.filter((action) => !action.field?.startsWith("var_") && !action.field?.startsWith("reference_var_") && action.field !== "addOutputReference" && action.field !== "errorHandling" && action.key !== "output-references-divider");

         return (
            <>
               <SoftBox sx={{ marginTop: 2 }}>
                  <SoftTypography variant="subtitle2">Variables</SoftTypography>
               </SoftBox>
                {variableActions.map((action) => getInputByType(action))}
                {nonVariableActions.map((action) => getInputByType(action))}
                <Divider sx={{ marginTop: 2.5, marginBottom: 1.5 }} />
                 <SoftBox>
                    <SoftTypography variant="subtitle2">Output References</SoftTypography>
                 </SoftBox>
                <List sx={{ pt: 1.5, pb: 0, display: "flex", flexDirection: "column", gap: 1.5 }}>
                   {outputReferenceActions.map((action) => getInputByType(action))}
                </List>
                {outputReferenceButton && getInputByType(outputReferenceButton)}
                {errorHandlingAction && (
                   <>
                      <Divider sx={{ marginTop: 2.5, marginBottom: 1.5 }} />
                      {getInputByType(errorHandlingAction)}
                   </>
                )}
            </>
         );
      }

      return isCommonComponent
         ? componentAction.dataInsideAction?.map((action) => getInputByType(action))
         : generateConnectorFields(componentAction.dataInsideAction ?? []);
   };

   return (
      <ActionFieldList
         style={{
            position: "relative",
            marginTop: typeToIncludeWithTransform.includes(firstActionType) ? "-16px" : "0",
            zIndex: 1000
         }}
         ref={actionFieldsRef}
         className="action-inputs"
         subheader={
            !isCommonComponent || isMultipleActions ? (
               <SoftTypography
                  sx={{
                     display: "flex",
                     justifyContent: "center",
                     alignItems: "center",
                     mt: "25px"
                  }}
                  variant="caption"
                  fontWeight="bold"
                  color="dark"
                  fontSize="18px"
               >
                  {componentAction.actionName}
               </SoftTypography>
            ) : null
         }
      >
      {generateFields()}
   </ActionFieldList>
   );
};
